import { getLocalStorage } from '@functions/localStorage';
import useIsSingleQuestPage from '@store/appRouter/hooks/useIsSingleQuestPage';
import useAddress from '@store/web3Store/hooks/useAddress';
import useConnectWallet from '@store/web3Store/hooks/useConnectWallet';
import useConnectedWeb3Account from '@store/web3Store/hooks/useConnectedWeb3Account';
import useDisconnectWallet from '@store/web3Store/hooks/useDisconnectWallet';
import useIsWalletConnected from '@store/web3Store/hooks/useIsWalletConnected';
import useProvider from '@store/web3Store/hooks/useProvider';
import useUpdateWeb3Account from '@store/web3Store/hooks/useUpdateWeb3Account';
import useWalletId from '@store/web3Store/hooks/useWalletId';
import { EWalletId, EWalletType } from '@store/web3Store/web3Store.enums';
import { getWalletFromId } from '@store/web3Store/web3Store.functions';
import {
	CONNECTED_WEB3_ACCOUNTS_KEY,
	NETWORKS_DATA,
	SAVED_USER_ADDRESS_KEY,
	WALLETS_ARRAY
} from '@store/web3Store/web3Store.variables';
import { useCallback, useEffect, useRef } from 'react';

export default function useEVMState() {
	const walletType = EWalletType.ethereum;

	const [address] = useAddress(walletType);
	const [provider] = useProvider(walletType);
	const [walletId] = useWalletId(walletType);
	const [connectedWeb3Account] = useConnectedWeb3Account(walletType);
	const [isSingleQuestPage] = useIsSingleQuestPage();
	const [isWalletConnected] = useIsWalletConnected(walletType);
	const { disconnectWallet } = useDisconnectWallet(walletType);
	const { updateWeb3Account } = useUpdateWeb3Account();
	const { connectWallet } = useConnectWallet();

	const refreshTimeout = useRef<NodeJS.Timeout>();

	const updateChainId = useCallback(
		async (chainId?: number) => {
			if (!isWalletConnected || !provider?.web3Instance || !connectedWeb3Account) {
				return;
			}

			//const newNetworkId = chainId || (await provider.web3Instance?.request?.({ method: 'eth_chainId' })) || undefined;
			const newNetworkId = chainId || (await provider.web3Instance.getChainId()) || undefined;

			if (newNetworkId) {
				if (`${newNetworkId}`.startsWith('0x')) {
					const intNetworkId = parseInt(newNetworkId.toString(), 16);

					updateWeb3Account(connectedWeb3Account.id, {
						chainId: intNetworkId
					});
					return;
				}

				updateWeb3Account(connectedWeb3Account.id, {
					chainId: newNetworkId
				});
			} else {
				console.error("can't get chain id");

				if (process.env.BUILD_ENV === 'test') {
					console.warn('using sepolia chain id for test env');
					updateWeb3Account(connectedWeb3Account.id, {
						chainId: NETWORKS_DATA.sepolia.chainId
					});
					return;
				}

				updateWeb3Account(connectedWeb3Account.id, {
					chainId: undefined
				});
			}
		},
		[updateWeb3Account, provider?.web3Instance, isWalletConnected, connectedWeb3Account]
	);

	// check if the user was previously connected and connect them if true (after page refresh for example)
	const checkIfWalletIsConnected = useCallback(() => {
		const walletTypeIds = WALLETS_ARRAY.filter((wallet) => wallet.type === walletType).map((wallet) => wallet.id);

		const connectedWeb3AccountsId = getLocalStorage<EWalletId[]>(CONNECTED_WEB3_ACCOUNTS_KEY) || [];

		const savedWalletId = walletTypeIds.find((walletId) => connectedWeb3AccountsId.includes(walletId));

		if (!savedWalletId) return;

		const savedWallet = getWalletFromId(savedWalletId);

		if (savedWallet) connectWallet(savedWallet);
	}, [connectWallet, walletType]);

	useEffect(() => {
		// check if address is updated and disconnect the user if true
		if (address?.toLowercase && isWalletConnected) {
			const savedAddress = getLocalStorage(SAVED_USER_ADDRESS_KEY);

			if (savedAddress && savedAddress !== address?.toLowercase()) {
				disconnectWallet();
			}
		}
	}, [address, disconnectWallet, isWalletConnected]);

	const onChainChanged = useCallback(
		async (chainId: number): Promise<void> => {
			updateChainId(chainId);

			if (!walletId) return;

			// save wallet type
			const savedWallet = getWalletFromId(walletId);

			// disconnect wallet
			disconnectWallet();

			if (!savedWallet) return;

			// connect again (the idea is to update the provider )
			connectWallet(savedWallet);
		},
		[connectWallet, disconnectWallet, updateChainId, walletId]
	);

	const refreshAccount = useCallback(() => {
		refreshTimeout.current = setTimeout(() => {
			if (!walletId) return;

			// save wallet type
			const savedWallet = getWalletFromId(walletId);

			// disconnect wallet
			disconnectWallet();

			if (!savedWallet) return;

			// connect again (the idea is to update the provider )
			connectWallet(savedWallet);
		}, 200);
	}, [connectWallet, disconnectWallet, walletId]);

	useEffect(() => {
		if (!provider?.web3Instance) return;

		async function onAccountsChanged(accounts: string[]): Promise<void> {
			disconnectWallet();
		}

		try {
			if (!provider.web3Instance?.walletStateProvider?.on) {
				console.warn('provider.web3Instance.on is undefined');
			}

			if (!provider.web3Instance?.walletStateProvider?.removeListener) {
				console.warn('provider.web3Instance.removeListener is undefined');
			}

			provider.web3Instance?.walletStateProvider?.on('chainChanged', refreshAccount);
			provider.web3Instance?.walletStateProvider?.on('accountsChanged', refreshAccount);
		} catch (err) {
			console.error('Error while initializing web3 listeners', err);
		}

		return () => {
			provider.web3Instance?.walletStateProvider?.removeListener('chainChanged', refreshAccount);
			provider.web3Instance?.walletStateProvider?.removeListener('accountsChanged', refreshAccount);
		};
	}, [disconnectWallet, provider, updateChainId, onChainChanged, refreshAccount]);

	/* useEffect(() => {
		if (!isSingleQuestPage) return;

		// test to not call this function
		checkIfWalletIsConnected();
		//resetWalletProvidersLocalStorage();
	}, [checkIfWalletIsConnected, isSingleQuestPage]); */

	return null;
}
